import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { PanelState, shortcuts } from 'store/modules/panel_store/types';
import ServiceItem from 'components/atoms/service_item';
import { ProfileState } from 'store/modules/profile_store/types';
import { Link } from 'react-router-dom';
import ModalConLicitaIA from 'components/atoms/summary_notice_modal';
import LucideIcon from 'components/atoms/lucide_icon';
import { getRecentTools, registerRecentTool } from 'services/recent_tools';
import { canSeeTool } from 'services/alfa_tools';
import { isConlicitasegAccessibleTool } from 'services/conlicitaseg_tool';
import useOnlyConlicitaseg from 'hooks/useOnlyConlicitaseg';
import { TOOL_CATEGORIES } from './categories';
import { Container } from './styles';

type ModalType = 'resumo' | 'pergunte' | undefined;

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const ToolList: React.FC = () => {
  const { panel } = useSelector<ApplicationState, PanelState>((state) => state.session.panel);

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const [query, setQuery] = useState('');
  const [activeModal, setActiveModal] = useState<ModalType>(undefined);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [recentNames, setRecentNames] = useState<string[]>(getRecentTools);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isOnlyConlicitaseg = useOnlyConlicitaseg();

  useEffect(() => {
    if (window.matchMedia('(min-width: 769px)').matches) searchInputRef.current?.focus();
  }, []);

  const visibleTools = useMemo(() => {
    return (panel?.list_tools_shortcuts ?? [])
      .filter((service) => canSeeTool(service, profile))
      .filter((service) => !isOnlyConlicitaseg || isConlicitasegAccessibleTool(service));
  }, [
    panel?.list_tools_shortcuts,
    profile?.alfa_tester,
    profile?.clientId,
    profile?.ambassador,
    isOnlyConlicitaseg,
  ]);

  const q = normalize(query.trim());

  const groups = useMemo(() => {
    const filteredTools = q
      ? visibleTools.filter((tool) => normalize(tool.name).includes(q))
      : visibleTools;
    return TOOL_CATEGORIES.map((category) => ({
      ...category,
      items: filteredTools.filter((tool) => tool.category === category.name),
    })).filter((group) => group.items.length > 0);
  }, [visibleTools, q]);

  const recentTools = useMemo(
    () =>
      recentNames
        .map((name) => visibleTools.find((tool) => tool.name === name))
        .filter((tool): tool is shortcuts => Boolean(tool))
        .slice(0, 3),
    [recentNames, visibleTools],
  );

  const handleActivate = (name: string) => {
    registerRecentTool(name);
    setRecentNames(getRecentTools());
  };

  const openCategory = expandedCategory ?? groups[0]?.name;

  return (
    <Container className="position-absolute bg-white">
      <ul className="menu-mobile d-md-none">
        <li>
          <Link to="/" className="text-dark py-2 d-flex align-items-center">
            Dashboard
          </Link>
        </li>
        <li>
          <Link className="text-dark py-2 d-flex align-items-center" to="/ajuda">
            Ajuda
          </Link>
        </li>
      </ul>

      <div className="search-bar">
        <LucideIcon name="search" size={18} />
        <input
          ref={searchInputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar ferramentas..."
          aria-label="Buscar ferramentas"
        />
        {query && (
          <button
            type="button"
            className="clear-btn"
            aria-label="Limpar busca"
            onClick={() => {
              setQuery('');
              searchInputRef.current?.focus();
            }}
          >
            <LucideIcon name="x" size={17} />
          </button>
        )}
      </div>

      {!q && recentTools.length > 0 && (
        <div className="recents">
          <span className="recents-label">
            <LucideIcon name="clock" size={14} />
            Recentes
          </span>
          <ul className="recents-chips">
            {recentTools.map((service) => (
              <ServiceItem
                key={service.id}
                name={service.name}
                icon={service.icon}
                link={service?.link}
                tagNew={false}
                variant="chip"
                onOpenModal={setActiveModal}
                onActivate={handleActivate}
              />
            ))}
          </ul>
        </div>
      )}

      {q && groups.length === 0 && (
        <div className="empty-state">Nenhuma ferramenta encontrada para &ldquo;{query}&rdquo;.</div>
      )}

      <div className="tools-grid d-none d-md-block">
        {groups.map((group) => (
          <div className="category-block" key={group.name}>
            <div className="category-header">
              <span className="category-icon">
                <LucideIcon name={group.icon} size={17} />
              </span>
              <h6>{group.name}</h6>
            </div>
            <ul>
              {group.items.map((service) => (
                <ServiceItem
                  key={service.id}
                  name={service.name}
                  icon={service.icon}
                  link={service?.link}
                  tagNew={service?.tag_new}
                  onOpenModal={setActiveModal}
                  onActivate={handleActivate}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="tools-accordion d-md-none">
        {groups.map((group, index) => {
          const open = q ? true : openCategory === group.name;
          const panelId = `tools-accordion-panel-${index}`;
          return (
            <div className="accordion-section" key={group.name}>
              <button
                type="button"
                className={`accordion-head${open ? ' open' : ''}`}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setExpandedCategory(open ? '' : group.name)}
              >
                <span className="category-icon">
                  <LucideIcon name={group.icon} size={18} />
                </span>
                <span className="category-name">{group.name}</span>
                <span className="category-count">{group.items.length}</span>
                <LucideIcon name={open ? 'chevron-up' : 'chevron-down'} size={18} />
              </button>
              {open && (
                <ul id={panelId} className="accordion-items">
                  {group.items.map((service) => (
                    <ServiceItem
                      key={service.id}
                      name={service.name}
                      icon={service.icon}
                      link={service?.link}
                      tagNew={service?.tag_new}
                      onOpenModal={setActiveModal}
                      onActivate={handleActivate}
                    />
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <ModalConLicitaIA
        navbar
        show={activeModal === 'resumo'}
        title="Resumo do Edital"
        description={
          <>
            <p>
              Oferece acesso rápido aos dados essenciais dos editais, ajudando o licitante a decidir
              se a oportunidade vale a pena antes da leitura completa.
            </p>
            <p>
              Disponível em <a href="/minhas_licitacoes/management">Gerenciar Licitações</a>,{' '}
              <a href="/boletim_web/public/boletins">Boletim de Licitações</a> e{' '}
              <a href="/banco_de_dados">Encontrar Licitações</a>.
            </p>
          </>
        }
        onClose={() => setActiveModal(undefined)}
      />

      <ModalConLicitaIA
        navbar
        show={activeModal === 'pergunte'}
        title="Pergunte ao Edital"
        description={
          <>
            <p>
              Permite esclarecer dúvidas sobre exigências e riscos diretamente do edital, com
              rapidez e precisão.
            </p>
            <p>
              A IA pode ser utilizada pelas ferramentas{' '}
              <a href="/minhas_licitacoes/management">Gerenciar Licitações</a>,{' '}
              <a href="/boletim_web/public/boletins">Boletim de Licitações</a> e{' '}
              <a href="/banco_de_dados">Encontrar Licitações</a>.
            </p>
          </>
        }
        onClose={() => setActiveModal(undefined)}
      />
    </Container>
  );
};

export default ToolList;
